/* eslint-disable react-hooks/exhaustive-deps */
import React, {
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
  useRef,
} from "react";
import PropTypes from "prop-types";
import "mapbox-gl/dist/mapbox-gl.css";
import { useMediaQuery } from "react-responsive";
import { getBoundingPoints } from "helpers/maps";
import Map from "react-map-gl";
import MapMarkerStatic from "components/shared/stays/MapMarkerStatic";
import styles from "components/shared/stays/MapStatic.module.scss";

export default function MapStatic({
  trips = null,
  paddingLarge = 80,
  paddingSmall = 60,
}) {
  // Make sure all trips have a lat/lng
  const allTrips = trips.filter((t) => t.address_lat && t.address_lng);
  const mapboxApiAccessToken = process.env.NEXT_PUBLIC_MAPBOX_API_KEY;
  const initialZoom = 0;
  const defaultMaxZoom = 9;
  const defaultTransitionSpeed = 1000;
  const greaterThanLg = useMediaQuery({
    query: `(min-width: ${styles.solBreakpointLg}px)`,
  });
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 });
  const [isInitiated, setIsInitiated] = useState(false);
  const wrapperRef = useRef(null);
  const mapRef = useRef();

  function updateDimensions() {
    if (wrapperRef.current) {
      setDimensions({
        width: wrapperRef.current.offsetWidth,
        height: wrapperRef.current.offsetHeight,
      });
    }
  }

  // Note: useLayoutEffect must run after the initial render for SSR.
  // Be sure to wrap this component in a check set with useEffect on its parent.
  useLayoutEffect(() => {
    updateDimensions();
  }, []);

  const [viewState, setViewState] = useState({
    latitude: 10,
    longitude: 0,
    zoom: initialZoom,
  });

  function handleResize() {
    updateDimensions();
  }

  // Crop map in on visible trips
  function reframeMap() {
    if (allTrips.length > 0) {
      const mapPadding = greaterThanLg ? paddingLarge : paddingSmall;
      // crop map in on visible trips
      const boundingPoints = getBoundingPoints(allTrips);
      mapRef?.current?.fitBounds([boundingPoints[0], boundingPoints[1]], {
        padding: mapPadding,
        center: [
          (boundingPoints[0][0] + boundingPoints[1][0]) / 2,
          (boundingPoints[0][1] + boundingPoints[1][1]) / 2,
        ],
        maxZoom: defaultMaxZoom,
        duration: defaultTransitionSpeed,
      });
    }
  }

  useLayoutEffect(() => {
    // Set initial size
    if (isInitiated) {
      reframeMap();
    }
  }, [isInitiated, trips]);

  function checkInitiated() {
    if (dimensions?.height > 0 && mapRef.current) {
      setIsInitiated(true);
    } else {
      setTimeout(() => {
        checkInitiated();
      }, 50);
    }
  }

  // Listen for when DOM is ready and mapRef is available
  useEffect(() => {
    checkInitiated();
  }, [dimensions]);

  useEffect(() => {
    // Attach resize listener
    window.addEventListener("resize", handleResize);

    return function cleanup() {
      window.removeEventListener("resize", handleResize);
    };
  });

  const markers = useMemo(
    () =>
      allTrips.map((trip) => (
        <MapMarkerStatic
          key={trip.id}
          lowestPrice={trip.lowest_price_per_person_per_night}
          latitude={parseFloat(trip.address_lat, 10)}
          longitude={parseFloat(trip.address_lng, 10)}
        />
      )),
    [trips]
  );

  return (
    <div className={styles.mapWrapper} ref={wrapperRef}>
      {dimensions?.height > 0 && (
        <Map
          ref={mapRef}
          // eslint-disable-next-line react/jsx-props-no-spreading
          {...viewState}
          onMove={(evt) => setViewState(evt.viewState)}
          style={{ width: dimensions.width, height: dimensions.height }}
          mapStyle="mapbox://styles/rteuscher/cksxuwys72vmg18mncm7bg7mh"
          mapboxAccessToken={mapboxApiAccessToken}
          onRender={(event) => event.target.resize()}
          interactive={false}
        >
          {markers}
        </Map>
      )}
    </div>
  );
}

MapStatic.propTypes = {
  trips: PropTypes.arrayOf(
    PropTypes.shape({
      accommodation_features_list: PropTypes.arrayOf(PropTypes.string),
      address_city: PropTypes.string,
      address_lat: PropTypes.string,
      address_lng: PropTypes.string,
      available_scout_spots: PropTypes.number,
      avg_response_time_in_minutes: PropTypes.number,
      bwrag_certified: PropTypes.bool,
      categories_list: PropTypes.arrayOf(PropTypes.string),
      collection_list: PropTypes.arrayOf(PropTypes.string),
      country: PropTypes.string,
      distance: PropTypes.number,
      has_trip_reviews: PropTypes.bool,
      id: PropTypes.string,
      is_featured: PropTypes.bool,
      is_instantly_bookable: PropTypes.bool,
      is_visible: PropTypes.bool,
      lowest_price_per_person_per_night: PropTypes.number,
      lowest_product_pricing_type: PropTypes.string,
      main_image: PropTypes.shape({
        url: PropTypes.string,
        content_type: PropTypes.string,
        byte_size: PropTypes.number,
        width: PropTypes.number,
        height: PropTypes.number,
      }),
      name: PropTypes.string,
      operator_id: PropTypes.number,
      operator_is_visible: PropTypes.bool,
      operator_name: PropTypes.string,
      operator_slug: PropTypes.string,
      region: PropTypes.shape({
        name: PropTypes.string,
        world_area: PropTypes.string,
      }),
      regional_surf_season: PropTypes.arrayOf(PropTypes.string),
      review_scores: PropTypes.arrayOf(PropTypes.number),
      slug: PropTypes.string,
      surf_features_list: PropTypes.arrayOf(PropTypes.string),
      tag_list: PropTypes.arrayOf(PropTypes.string),
      trip_type_description: PropTypes.string,
    })
  ),
  paddingLarge: PropTypes.number,
  paddingSmall: PropTypes.number,
};
